import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { ClientUser, ClientUsersData, CreateClientUserPayload } from '../types/superadmin';

/** Which live action is in flight, and for whom. */
export type UserActionKind = 'toggle' | 'delete' | 'resend';
export interface BusyAction {
  kind: UserActionKind;
  userId: string;
}

export interface ClientUsersState {
  data: ClientUsersData | null;
  /** Users the backend has, which may exceed the one page we load. */
  totalUsers: number;
  loading: boolean;
  loadError: string | null;
  notice: string | null;
  actionError: string | null;
  creating: boolean;
  busy: BusyAction | null;
  clearMessages: () => void;
  createUser: (payload: CreateClientUserPayload) => Promise<boolean>;
  toggleUser: (user: ClientUser) => Promise<boolean>;
  deleteUser: (user: ClientUser) => Promise<boolean>;
  resendWelcome: (user: ClientUser) => Promise<boolean>;
}

/**
 * Live roster + user-management actions for one client. Reads and writes the
 * client's real data and reloads after every change.
 * Each action resolves true on success so the caller can close its confirm.
 */
export function useClientUsers(tenantId: string, clientName: string): ClientUsersState {
  const [data, setData] = useState<ClientUsersData | null>(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState<BusyAction | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.getClientUsers(tenantId);
      setData(res?.data ?? null);
      setTotalUsers(res?.meta?.count ?? res?.data?.users?.length ?? 0);
    } catch (error: any) {
      setLoadError(error?.message || 'Could not load users for this client.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const clearMessages = useCallback(() => {
    setNotice(null);
    setActionError(null);
  }, []);

  /** Shared run-and-report wrapper for the per-user actions. */
  const runUserAction = async (
    kind: UserActionKind,
    user: ClientUser,
    request: () => Promise<unknown>,
    success: string,
    failure: string,
    shouldReload: boolean
  ): Promise<boolean> => {
    setBusy({ kind, userId: user.id });
    clearMessages();
    try {
      await request();
      setNotice(success);
      if (shouldReload) await load();
      return true;
    } catch (error: any) {
      setActionError(error?.message || failure);
      return false;
    } finally {
      setBusy(null);
    }
  };

  const createUser = async (payload: CreateClientUserPayload): Promise<boolean> => {
    setCreating(true);
    clearMessages();
    try {
      await api.createClientUser(tenantId, payload);
      setNotice(`${payload.name} can now sign in at ${clientName}.`);
      await load();
      return true;
    } catch (error: any) {
      setActionError(error?.message || 'Could not create the user.');
      return false;
    } finally {
      setCreating(false);
    }
  };

  const toggleUser = (user: ClientUser) =>
    runUserAction(
      'toggle',
      user,
      () => api.toggleClientUser(tenantId, user.id),
      user.isActive
        ? `${user.name} has been deactivated and no longer has access.`
        : `${user.name} has been reactivated and can sign in again.`,
      user.isActive ? 'Could not deactivate the user.' : 'Could not reactivate the user.',
      true
    );

  const deleteUser = (user: ClientUser) =>
    runUserAction(
      'delete',
      user,
      () => api.deleteClientUser(tenantId, user.id),
      `${user.name} has been permanently deleted.`,
      'Could not delete the user.',
      true
    );

  const resendWelcome = (user: ClientUser) =>
    runUserAction(
      'resend',
      user,
      () => api.resendClientUserWelcome(tenantId, user.id),
      `${user.name} has been emailed a new temporary password.`,
      'Could not resend the welcome email.',
      false
    );

  return {
    data,
    totalUsers,
    loading,
    loadError,
    notice,
    actionError,
    creating,
    busy,
    clearMessages,
    createUser,
    toggleUser,
    deleteUser,
    resendWelcome
  };
}
